import React, { FC } from 'react';
import { gql, useQuery } from '@apollo/client';
import { endOfMonth, subMonths } from 'date-fns';
import {
  Bar,
  ComposedChart,
  Line,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from 'recharts';
import { Kpi } from '../../../../../dto/Kpi';
import { Widget } from '../Widget';
import { aggregateByMonth } from './WidgetCalculationUtils';

const SKONTO_QUERY = gql`
  query($startDate: Float, $endDate: Float) {
    kpiSix(startDate: $startDate, endDate: $endDate) {
      Kpi
      KpiDate
      Counter
      Netvalue
    }
  }
`;

export const RechnungsEingangProMonat: FC = () => {
  const endDate = endOfMonth(new Date());
  const startDate = subMonths(endDate, 12);

  const { data } = useQuery<{ kpiSix: Kpi[] }>(SKONTO_QUERY, {
    variables: { startDate: startDate.getTime(), endDate: endDate.getTime() },
  });

  const chartData = aggregateByMonth(startDate, endDate, data?.kpiSix || []);

  const monthTickFormatter = (tick: string): string => {
    const date = new Date(tick);
    const month = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'Mai',
      'Jun',
      'Jul',
      'Aug',
      'Sept',
      'Okt',
      'Nov',
      'Dez',
    ];
    return `${month[date.getMonth()]}`;
  };

  const labelFormatter = (tick: string): string => {
    const date = new Date(tick);
    const month = [
      'Jänner',
      'Februrar',
      'März',
      'April',
      'Mai',
      'Juni',
      'Juli',
      'August',
      'September',
      'Oktober',
      'November',
      'Dezember',
    ];
    return `${month[date.getMonth()]}`;
  };

  const formatTooltipValues = (value: number, label: string): string => {
    if (label === 'Anzahl Rechnungen') {
      return `${value}`;
    }
    if (label === 'Gesamtbetrag') {
      return `${value.toFixed(2)} €`;
    }
    return '';
  };

  return (
    <Widget
      width={6}
      height={3}
      title="Rechnungen pro Monat"
      subtitle="12 Monate Rückblick"
    >
      <ComposedChart width={550} height={230} data={Object.values(chartData)}>
        <Legend />
        <XAxis
          tickCount={5}
          dataKey="date"
          tickFormatter={monthTickFormatter}
          scale="band"
          style={{ fontSize: '.8em' }}
        />
        <YAxis
          tickCount={5}
          yAxisId={0}
          style={{ fontSize: '.8em' }}
          tickFormatter={(tick: number) => `${tick} €`}
        />
        <YAxis
          orientation="right"
          yAxisId={1}
          minTickGap={1}
          tickCount={3}
          style={{ fontSize: '.8em' }}
        />
        <Tooltip
          cursor={{ stroke: '#000000' }}
          labelFormatter={labelFormatter}
          formatter={(value: number, label: string) =>
            formatTooltipValues(value, label)
          }
        />
        <CartesianGrid stroke="#f5f5f5" />
        <Bar
          dataKey="value"
          name="Gesamtbetrag"
          fill="#ccc"
          yAxisId={0}
          barSize={8}
        />
        <Line
          dataKey="count"
          name="Anzahl Rechnungen"
          fill="#4682DA"
          yAxisId={1}
        />
      </ComposedChart>
    </Widget>
  );
};
